/**
 * Live messages for one chat, plus sending and pagination.
 *
 * Works identically on every backend because it only touches the services
 * layer - swapping VITE_BACKEND changes nothing here.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { messages as messageService } from '@zivapp/shared'

export function useMessages(chatId, { limit = 50 } = {}) {
  const [messages, setMessages] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [hasMore, setHasMore] = useState(true)
  const [error, setError] = useState(null)
  const isLoadingOlder = useRef(false)

  useEffect(() => {
    if (!chatId) {
      setMessages([])
      setIsLoading(false)
      return undefined
    }
    setIsLoading(true)
    setHasMore(true)

    const unsubscribe = messageService.subscribe(chatId, list => {
      setMessages(list)
      setIsLoading(false)
    }, { limit })

    return unsubscribe
  }, [chatId, limit])

  const send = useCallback(async options => {
    setError(null)
    try {
      return await messageService.send(chatId, options)
    } catch (caught) {
      setError(caught)
      throw caught
    }
  }, [chatId])

  const loadOlder = useCallback(async () => {
    if (isLoadingOlder.current || !hasMore || !messages.length) return
    isLoadingOlder.current = true
    try {
      const older = await messageService.loadOlder(chatId, messages[0].id)
      if (!older.length) setHasMore(false)
      else setMessages(current => [...older, ...current])
    } finally {
      isLoadingOlder.current = false
    }
  }, [chatId, messages, hasMore])

  return { messages, isLoading, hasMore, error, send, loadOlder }
}
